/**
 * Extended API endpoints for Expense Invoices
 * Adds paginated listing and payment tracking operations
 */

import { expenseInvoicesApi } from './expenseInvoicesApi';
import { TagTypes } from '../../../Api/Types/tagTypes';
import {
  ExpenseInvoicePagedListDto,
  ExpenseInvoiceQueryParams,
  ExpenseInvoiceDto,
  CreateExpenseInvoiceDto,
  UpdateExpenseInvoiceDto,
  CreateSimplifiedExpenseInvoiceDto,
  UpdateSimplifiedExpenseInvoiceDto,
  BookingAssignmentDto,
  BookingSuggestionDto,
  ExpenseInvoiceSettlementPreviewDto,
  SettleExpenseInvoiceFromPrepaymentRequest,
  SupplierPrepaymentMutationResultDto,
  ExpenseInvoiceSettlementListItemDto,
  ReleaseSupplierPrepaymentAllocationRequest,
  FinalizeExpenseInvoiceImportReviewResultDto,
} from '../Types/expenseInvoice.types';
import {
  buildCreateExpenseInvoiceRequest,
  buildCreateSimplifiedExpenseInvoiceRequest,
  buildUpdateExpenseInvoiceRequest,
  buildUpdateSimplifiedExpenseInvoiceRequest,
} from './expenseInvoiceRequestContracts';
import {
  normalizeExpenseInvoiceDetails,
  normalizeExpenseInvoicePagedList,
} from './expenseInvoiceResponseMapper';
import { mapBookingSuggestionsResponse } from './expenseInvoiceBookingSuggestionsMapper';
import type { BookingSuggestion } from './expenseInvoiceBookingSuggestions.contract';

const extendedExpenseInvoicesApi = expenseInvoicesApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * GET /api/admin/expense-invoices/{id}
     * Pobiera szczegóły pojedynczej faktury kosztowej
     */
    getExpenseInvoiceById: builder.query<ExpenseInvoiceDto, number>({
      query: (id) => ({
        url: `/expense-invoices/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: ExpenseInvoiceDto) => normalizeExpenseInvoiceDetails(response),
      providesTags: (result, error, id) => [{ type: TagTypes.ExpenseInvoice, id }],
    }),
    /**
     * GET /api/admin/expense-invoices
     * Paginated list with filters - backend returns IPagedList
     */
    getExpenseInvoicesPaged: builder.query<ExpenseInvoicePagedListDto, ExpenseInvoiceQueryParams>({
      query: (params) => ({
        url: '/expense-invoices',
        method: 'GET',
        params: {
          pageNumber: params.pageNumber ?? 1,
          pageSize: params.pageSize ?? 10,
          ...(params.companyId && { companyId: params.companyId }),
          ...(params.status && { status: params.status }),
          ...(params.isPaid !== undefined ? { isPaid: params.isPaid } : {}),
          ...(params.supplierName && { supplierName: params.supplierName }),
          ...(params.issueDateFrom && { issueDateFrom: params.issueDateFrom }),
          ...(params.issueDateTo && { issueDateTo: params.issueDateTo }),
          ...(params.searchTerm && { searchTerm: params.searchTerm }),
          ...(params.invoiceNumber && { invoiceNumber: params.invoiceNumber }),
          ...(params.supplierCity && { supplierCity: params.supplierCity }),
          ...(params.documentKind && { documentKind: params.documentKind }),
          ...(params.isAssignedToAnyBooking !== undefined ? { isAssignedToAnyBooking: params.isAssignedToAnyBooking } : {}),
        },
      }),
      transformResponse: (response: ExpenseInvoicePagedListDto) =>
        normalizeExpenseInvoicePagedList(response),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({
                type: TagTypes.ExpenseInvoice as const,
                id,
              })),
              { type: TagTypes.ExpenseInvoice, id: TagTypes.List },
            ]
          : [{ type: TagTypes.ExpenseInvoice, id: TagTypes.List }],
    }),

    /**
     * POST /api/admin/expense-invoices
     * Creates a new expense invoice
     */
    createExpenseInvoice: builder.mutation<ExpenseInvoiceDto, CreateExpenseInvoiceDto>({
      query: (body) => buildCreateExpenseInvoiceRequest(body),
      transformResponse: (response: ExpenseInvoiceDto) => normalizeExpenseInvoiceDetails(response),
      invalidatesTags: [{ type: TagTypes.ExpenseInvoice, id: TagTypes.List }],
    }),

    createSimplifiedExpenseInvoice: builder.mutation<
      ExpenseInvoiceDto,
      CreateSimplifiedExpenseInvoiceDto
    >({
      query: (body) => buildCreateSimplifiedExpenseInvoiceRequest(body),
      transformResponse: (response: ExpenseInvoiceDto) => normalizeExpenseInvoiceDetails(response),
      invalidatesTags: [{ type: TagTypes.ExpenseInvoice, id: TagTypes.List }],
    }),

    /**
     * PUT /api/admin/expense-invoices/{id}
     * Updates an existing expense invoice
     */
    updateExpenseInvoice: builder.mutation<void, { id: number; data: UpdateExpenseInvoiceDto }>({
      query: ({ id, data }) => buildUpdateExpenseInvoiceRequest({ id, data }),
      invalidatesTags: (result, error, { id }) => [
        { type: TagTypes.ExpenseInvoice, id },
        { type: TagTypes.ExpenseInvoice, id: TagTypes.List },
      ],
    }),

    updateSimplifiedExpenseInvoice: builder.mutation<
      void,
      { id: number; data: UpdateSimplifiedExpenseInvoiceDto }
    >({
      query: ({ id, data }) => buildUpdateSimplifiedExpenseInvoiceRequest({ id, data }),
      invalidatesTags: (result, error, { id }) => [
        { type: TagTypes.ExpenseInvoice, id },
        { type: TagTypes.ExpenseInvoice, id: TagTypes.List },
      ],
    }),

    /**
     * PUT /api/admin/expense-invoices/{id}/assign-bookings
     * Assigns bookings to an expense invoice with allocated amounts
     * Replaces all existing assignments
     */
    assignBookingsToExpenseInvoice: builder.mutation<
      void,
      { invoiceId: number; assignments: BookingAssignmentDto[] }
    >({
      query: ({ invoiceId, assignments }) => ({
        url: `/expense-invoices/${invoiceId}/assign-bookings`,
        method: 'PUT',
        body: assignments,
      }),
      invalidatesTags: (result, error, { invoiceId, assignments }) => {
        const bookingIds = Array.from(
          new Set(
            (assignments ?? [])
              .map((assignment) => assignment.bookingId)
              .filter((bookingId) => Number.isInteger(bookingId) && bookingId > 0)
          )
        );

        return [
          { type: TagTypes.ExpenseInvoice, id: invoiceId },
          { type: TagTypes.ExpenseInvoice, id: TagTypes.List },
          ...bookingIds.map((bookingId) => ({
            type: TagTypes.Booking as const,
            id: bookingId,
          })),
          { type: TagTypes.Booking, id: TagTypes.List },
        ];
      },
    }),

    /**
     * GET /api/admin/expense-invoices/{id}/booking-suggestions
     * Returns up to 3 ranked, explainable booking suggestions for an expense invoice.
     * Read-only: selecting a suggestion never persists an assignment by itself.
     */
    getBookingSuggestionsForExpenseInvoice: builder.query<BookingSuggestion[], number>({
      query: (invoiceId) => ({
        url: `/expense-invoices/${invoiceId}/booking-suggestions`,
        method: 'GET',
      }),
      transformResponse: (response: BookingSuggestionDto[]) =>
        mapBookingSuggestionsResponse(response),
      providesTags: (result, error, invoiceId) => [
        { type: TagTypes.ExpenseInvoice, id: invoiceId },
      ],
    }),

    /**
     * GET /api/admin/expense-invoices/{id}/supplier-prepayment-preview
     * Returns due amount and open prepayment balance for settlement proposal
     */
    getSupplierPrepaymentPreview: builder.query<ExpenseInvoiceSettlementPreviewDto, number>({
      query: (invoiceId) => ({
        url: `/expense-invoices/${invoiceId}/supplier-prepayment-preview`,
        method: 'GET',
      }),
      providesTags: (result, error, invoiceId) => [
        { type: TagTypes.ExpenseInvoice, id: invoiceId },
      ],
    }),

    /**
     * GET /api/admin/expense-invoices/{id}/supplier-prepayment-settlements
     * Returns settlement rows with settlement identifiers required for release action
     */
    getSupplierPrepaymentSettlements: builder.query<ExpenseInvoiceSettlementListItemDto[], number>({
      query: (invoiceId) => ({
        url: `/expense-invoices/${invoiceId}/supplier-prepayment-settlements`,
        method: 'GET',
      }),
      providesTags: (result, error, invoiceId) => [
        { type: TagTypes.ExpenseInvoice, id: invoiceId },
      ],
    }),

    /**
     * POST /api/admin/expense-invoices/{id}/settle-from-prepayment
     * Settles expense invoice amount from supplier prepayment ledger
     */
    settleExpenseInvoiceFromPrepayment: builder.mutation<
      SupplierPrepaymentMutationResultDto,
      SettleExpenseInvoiceFromPrepaymentRequest
    >({
      query: ({
        invoiceId,
        amount,
        createdByUserId,
        description,
        bankDocumentEntryId,
        bookingId,
      }) => ({
        url: `/expense-invoices/${invoiceId}/settle-from-prepayment`,
        method: 'POST',
        params: {
          amount,
          createdByUserId,
          ...(description ? { description } : {}),
          ...(bankDocumentEntryId ? { bankDocumentEntryId } : {}),
          ...(bookingId ? { bookingId } : {}),
        },
      }),
      invalidatesTags: (result, error, { invoiceId }) => [
        { type: TagTypes.ExpenseInvoice, id: invoiceId },
        { type: TagTypes.ExpenseInvoice, id: TagTypes.List },
      ],
    }),

    /**
     * POST /api/admin/expense-invoices/{id}/finalize-import-review
     * Finalizes an OCR-imported expense invoice draft pending operator review,
     * transitioning it from Draft to Received (R9). The only supported way to make this
     * transition — the generic PUT update rejects a direct status change while blocked.
     */
    finalizeExpenseInvoiceImportReview: builder.mutation<
      FinalizeExpenseInvoiceImportReviewResultDto,
      number
    >({
      query: (id) => ({
        url: `/expense-invoices/${id}/finalize-import-review`,
        method: 'POST',
      }),
      // RTK Query calls invalidatesTags on every settled outcome, not only success — a
      // rejected (409) finalize must never invalidate the cache.
      invalidatesTags: (result, error, id) => {
        if (error) {
          return [];
        }

        return [
          { type: TagTypes.ExpenseInvoice, id },
          { type: TagTypes.ExpenseInvoice, id: TagTypes.List },
        ];
      },
    }),

    /**
     * POST /api/admin/supplier-prepayments/allocations/{settlementId}/release
     * Releases existing supplier prepayment allocation and restores open balance
     */
    releaseSupplierPrepaymentAllocation: builder.mutation<
      SupplierPrepaymentMutationResultDto,
      ReleaseSupplierPrepaymentAllocationRequest
    >({
      query: ({ settlementId, createdByUserId, description }) => ({
        url: `/supplier-prepayments/allocations/${settlementId}/release`,
        method: 'POST',
        params: {
          createdByUserId,
          ...(description ? { description } : {}),
        },
      }),
      invalidatesTags: (result) => {
        const tags: Array<{ type: TagTypes; id: number | string }> = [
          { type: TagTypes.ExpenseInvoice, id: TagTypes.List },
        ];

        if (result?.expenseInvoiceId) {
          tags.push({ type: TagTypes.ExpenseInvoice, id: result.expenseInvoiceId });
        }

        return tags;
      },
    }),
  }),
});

// Export extended hooks (aliased)
export const {
  useGetExpenseInvoicesPagedQuery: useExpenseInvoicesListQuery,
  useGetExpenseInvoiceByIdQuery: useExpenseInvoiceQuery,
  useLazyGetExpenseInvoiceByIdQuery: useLazyExpenseInvoiceQuery,
  useCreateExpenseInvoiceMutation: useCreateExpenseInvoice,
  useCreateSimplifiedExpenseInvoiceMutation: useCreateSimplifiedExpenseInvoice,
  useUpdateExpenseInvoiceMutation: useUpdateExpenseInvoice,
  useUpdateSimplifiedExpenseInvoiceMutation: useUpdateSimplifiedExpenseInvoice,
  useAssignBookingsToExpenseInvoiceMutation: useAssignExpenseInvoiceBookings,
  useGetBookingSuggestionsForExpenseInvoiceQuery: useExpenseInvoiceBookingSuggestionsQuery,
  useGetSupplierPrepaymentPreviewQuery: useExpenseInvoiceSupplierPrepaymentPreviewQuery,
  useGetSupplierPrepaymentSettlementsQuery: useExpenseInvoiceSupplierPrepaymentSettlementsQuery,
  useSettleExpenseInvoiceFromPrepaymentMutation: useSettleExpenseInvoiceFromPrepayment,
  useReleaseSupplierPrepaymentAllocationMutation: useReleaseSupplierPrepaymentAllocation,
  useFinalizeExpenseInvoiceImportReviewMutation,
} = extendedExpenseInvoicesApi;
